import { lazy, Suspense } from 'react';
import { Outlet } from 'react-router-dom';
// auth
import { AuthGuard, RoleBasedGuard, ClaimFlowGuard } from 'src/auth/guard';
// layouts
import DashboardLayout from 'src/layouts/dashboard';
// components
import { LoadingScreen } from 'src/components/loading-screen';

// ----------------------------------------------------------------------

// OVERVIEW
// const IndexPage = lazy(() => import('src/pages/dashboard/analytics'));
const IndexPage = lazy(() => import('src/pages/dashboard/app'));
const OverviewEcommercePage = lazy(() => import('src/pages/dashboard/ecommerce'));
const OverviewAnalyticsPage = lazy(() => import('src/pages/dashboard/analytics'));
const OverviewBankingPage = lazy(() => import('src/pages/dashboard/banking'));
const OverviewBookingPage = lazy(() => import('src/pages/dashboard/booking'));
const OverviewFilePage = lazy(() => import('src/pages/dashboard/file'));
// PRODUCT
const ProductDetailsPage = lazy(() => import('src/pages/dashboard/product/details'));
const ProductListPage = lazy(() => import('src/pages/dashboard/product/list'));
const ProductCreatePage = lazy(() => import('src/pages/dashboard/product/new'));
const ProductEditPage = lazy(() => import('src/pages/dashboard/product/edit'));
// ORDER
const OrderListPage = lazy(() => import('src/pages/dashboard/order/list'));
const OrderDetailsPage = lazy(() => import('src/pages/dashboard/order/details'));
// INVOICE
const InvoiceListPage = lazy(() => import('src/pages/dashboard/invoice/list'));
const InvoiceDetailsPage = lazy(() => import('src/pages/dashboard/invoice/details'));
const InvoiceCreatePage = lazy(() => import('src/pages/dashboard/invoice/new'));
const InvoiceEditPage = lazy(() => import('src/pages/dashboard/invoice/edit'));
// USER
const UserProfilePage = lazy(() => import('src/pages/dashboard/user/profile'));
const UserCardsPage = lazy(() => import('src/pages/dashboard/user/cards'));
const UserListPage = lazy(() => import('src/pages/dashboard/user/list'));
const UserAccountPage = lazy(() => import('src/pages/dashboard/user/account'));
const UserCreatePage = lazy(() => import('src/pages/dashboard/user/new'));
const UserEditPage = lazy(() => import('src/pages/dashboard/user/edit'));
// COMPANY
const CompanyListPage = lazy(() => import('src/pages/dashboard/company/list'));
// WORKFLOW
const ReactFlowPage = lazy(() => import('src/pages/dashboard/react-flow/board'));
// SCHEDULER
const SchedulerNewPage = lazy(() => import('src/pages/dashboard/scheduler/new'));
const SchedulerListPage = lazy(() => import('src/pages/dashboard/scheduler/list'));
const SchedulerEditPage = lazy(() => import('src/pages/dashboard/scheduler/edit'));
const SchedulerViewPage = lazy(() => import('src/pages/dashboard/scheduler/view'));
// DESIGNATION
const DesignationNewPage = lazy(() => import('src/pages/dashboard/designation/new'));
const DesignationListPage = lazy(() => import('src/pages/dashboard/designation/list'));
const DesignationEditPage = lazy(() => import('src/pages/dashboard/designation/edit'));
const DesignationViewPage = lazy(() => import('src/pages/dashboard/designation/view'));
// BLOG
const BlogPostsPage = lazy(() => import('src/pages/dashboard/post/list'));
const BlogPostPage = lazy(() => import('src/pages/dashboard/post/details'));
const BlogNewPostPage = lazy(() => import('src/pages/dashboard/post/new'));
const BlogEditPostPage = lazy(() => import('src/pages/dashboard/post/edit'));
// JOB
const JobDetailsPage = lazy(() => import('src/pages/dashboard/job/details'));
const JobListPage = lazy(() => import('src/pages/dashboard/job/list'));
const JobCreatePage = lazy(() => import('src/pages/dashboard/job/new'));
const JobEditPage = lazy(() => import('src/pages/dashboard/job/edit'));
// TOUR
const TourDetailsPage = lazy(() => import('src/pages/dashboard/tour/details'));
const TourListPage = lazy(() => import('src/pages/dashboard/tour/list'));
const TourCreatePage = lazy(() => import('src/pages/dashboard/tour/new'));
const TourEditPage = lazy(() => import('src/pages/dashboard/tour/edit'));
// FILE MANAGER
const FileManagerPage = lazy(() => import('src/pages/dashboard/file-manager'));
// APP
const ChatPage = lazy(() => import('src/pages/dashboard/chat'));
const MailPage = lazy(() => import('src/pages/dashboard/mail'));
const CalendarPage = lazy(() => import('src/pages/dashboard/calendar'));
const KanbanPage = lazy(() => import('src/pages/dashboard/kanban'));
// TEST RENDER PAGE BY ROLE
const PermissionDeniedPage = lazy(() => import('src/pages/dashboard/permission'));
// BLANK PAGE
const BlankPage = lazy(() => import('src/pages/dashboard/blank'));

// CLAIM FLOW (CUSTOM)
const PatientRegistrationPage = lazy(() => import('src/pages/dashboard/patient-registration'));
const DocumentCollectionPage = lazy(() => import('src/pages/dashboard/document-collection'));
const AIProcessingPage = lazy(() => import('src/pages/dashboard/ai-processing'));
const PolicyCoveragePage = lazy(() => import('src/pages/dashboard/policy-coverage'));
const AIVerificationPage = lazy(() => import('src/pages/dashboard/ai-verification'));
const FaceRecognitionPage = lazy(() => import('src/pages/dashboard/face-recognition'));
const ApprovalWorkflowPage = lazy(() => import('src/pages/dashboard/approval-workflow'));
const FinancePage = lazy(() => import('src/pages/dashboard/finance'));
const ClaimClosurePage = lazy(() => import('src/pages/dashboard/claim-closure'));
const CommunicationsPage = lazy(() => import('src/pages/dashboard/communications'));
const PatientCardGeneratedPage = lazy(() => import('src/pages/dashboard/patient-card-generated'));
const ClaimsListPage = lazy(() => import('src/pages/dashboard/claims'));
const AllPatientsPage = lazy(() => import('src/pages/dashboard/all-patients'));
const CfoReceiptsPage = lazy(() => import('src/pages/dashboard/cfo-receipts'));

// ----------------------------------------------------------------------

export const dashboardRoutes = [
  {
    path: 'dashboard',
    element: (
      <AuthGuard>
        <DashboardLayout>
          <Suspense fallback={<LoadingScreen />}>
            <Outlet />
          </Suspense>
        </DashboardLayout>
      </AuthGuard>
    ),
    children: [
      { element: <IndexPage />, index: true },
      // { path: 'ecommerce', element: <OverviewEcommercePage /> },
      {
        path: 'analytics',
        element: (
          <RoleBasedGuard hasContent roles={['admin']}>
            <OverviewAnalyticsPage />
          </RoleBasedGuard>
        ),
      },
      {
        path: 'claims',
        element: (
          <RoleBasedGuard hasContent roles={['admin']}>
            <ClaimsListPage />
          </RoleBasedGuard>
        ),
      },
      {
        path: 'all-patients',
        element: (
          <RoleBasedGuard hasContent roles={['admin']}>
            <AllPatientsPage />
          </RoleBasedGuard>
        ),
      },
      {
        path: 'cfo-receipts',
        element: (
          <RoleBasedGuard hasContent roles={['cfo']}>
            <CfoReceiptsPage />
          </RoleBasedGuard>
        ),
      },
      // Claim flow routes (match sidebar)
      {
        path: 'patient-registration',
        element: (
          <RoleBasedGuard hasContent roles={['admin']}>
            <PatientRegistrationPage />
          </RoleBasedGuard>
        ),
      },
      { path: 'face-recognition', element: <FaceRecognitionPage /> },
      {
        path: 'document-collection',
        element: (
          <RoleBasedGuard hasContent roles={['admin']}>
            <ClaimFlowGuard step="documentCollection">
              <DocumentCollectionPage />
            </ClaimFlowGuard>
          </RoleBasedGuard>
        ),
      },
      { path: 'patient-card-generated', element: <PatientCardGeneratedPage /> },
      {
        path: 'ai-processing',
        element: (
          <RoleBasedGuard hasContent roles={['admin']}>
            <ClaimFlowGuard step="aiProcessing">
              <AIProcessingPage />
            </ClaimFlowGuard>
          </RoleBasedGuard>
        ),
      },
      {
        path: 'policy-coverage',
        element: (
          <RoleBasedGuard hasContent roles={['admin']}>
            <ClaimFlowGuard step="policyCoverage">
              <PolicyCoveragePage />
            </ClaimFlowGuard>
          </RoleBasedGuard>
        ),
      },
      {
        path: 'ai-verification',
        element: (
          <RoleBasedGuard hasContent roles={['admin']}>
            <ClaimFlowGuard step="aiVerification">
              <AIVerificationPage />
            </ClaimFlowGuard>
          </RoleBasedGuard>
        ),
      },
      {
        path: 'approval-workflow',
        element: (
          <RoleBasedGuard hasContent roles={['admin']}>
            <ClaimFlowGuard step="approvalWorkflow">
              <ApprovalWorkflowPage />
            </ClaimFlowGuard>
          </RoleBasedGuard>
        ),
      },
      {
        path: 'finance',
        element: (
          <RoleBasedGuard hasContent roles={['admin']}>
            <ClaimFlowGuard step="finance">
              <FinancePage />
            </ClaimFlowGuard>
          </RoleBasedGuard>
        ),
      },
      {
        path: 'claim-closure',
        element: (
          <RoleBasedGuard hasContent roles={['admin']}>
            <ClaimFlowGuard step="claimClosure">
              <ClaimClosurePage />
            </ClaimFlowGuard>
          </RoleBasedGuard>
        ),
      },
      // {
      //   path: 'communications',
      //   element: (
      //     <RoleBasedGuard hasContent roles={['admin']}>
      //       <CommunicationsPage />
      //     </RoleBasedGuard>
      //   ),
      // },
      // { path: 'banking', element: <OverviewBankingPage /> },
      // { path: 'booking', element: <OverviewBookingPage /> },
      // { path: 'file', element: <OverviewFilePage /> },
      {
        path: 'user',
        children: [
          { element: <UserProfilePage />, index: true },
          { path: 'profile', element: <UserProfilePage /> },
          { path: 'cards', element: <UserCardsPage /> },
          { path: 'list', element: <UserListPage /> },
          { path: 'new', element: <UserCreatePage /> },
          { path: ':id/edit', element: <UserEditPage /> },
          { path: 'account', element: <UserAccountPage /> },
        ],
      },
      {
        path: 'company',
        children: [
          { element: <CompanyListPage />, index: true },
          { path: 'list', element: <CompanyListPage /> },
        ],
      },
      {
        path: 'product',
        children: [
          { element: <ProductListPage />, index: true },
          { path: 'list', element: <ProductListPage /> },
          { path: ':id', element: <ProductDetailsPage /> },
          { path: 'new', element: <ProductCreatePage /> },
          { path: ':id/edit', element: <ProductEditPage /> },
        ],
      },
      {
        path: 'order',
        children: [
          { element: <OrderListPage />, index: true },
          { path: 'list', element: <OrderListPage /> },
          { path: ':id', element: <OrderDetailsPage /> },
        ],
      },

      {
        path: 'reactflow',
        children: [
          { element: <ReactFlowPage />, index: true },
          { path: 'list', element: <ReactFlowPage /> },
          // { path: ':id', element: <InvoiceDetailsPage /> },
          // { path: ':id/edit', element: <InvoiceEditPage /> },
          // { path: 'new', element: <InvoiceCreatePage /> },
        ],
      },

      {
        path: 'scheduler',
        children: [
          { element: <SchedulerListPage />, index: true },
          { path: 'list', element: <SchedulerListPage /> },
          { path: ':id', element: <SchedulerViewPage /> },
          { path: ':id/edit', element: <SchedulerEditPage /> },
          { path: 'new', element: <SchedulerNewPage /> },
        ],
      },
      {
        path: 'designation',
        children: [
          { element: <DesignationListPage />, index: true },
          { path: 'list', element: <DesignationListPage /> },
          { path: ':id', element: <DesignationViewPage /> },
          { path: ':id/edit', element: <DesignationEditPage /> },
          { path: 'new', element: <DesignationNewPage /> },
        ],
      },
      {
        path: 'invoice',
        children: [
          { element: <InvoiceListPage />, index: true },
          { path: 'list', element: <InvoiceListPage /> },
          { path: ':id', element: <InvoiceDetailsPage /> },
          { path: ':id/edit', element: <InvoiceEditPage /> },
          { path: 'new', element: <InvoiceCreatePage /> },
        ],
      },
      {
        path: 'post',
        children: [
          { element: <BlogPostsPage />, index: true },
          { path: 'list', element: <BlogPostsPage /> },
          { path: ':title', element: <BlogPostPage /> },
          { path: ':title/edit', element: <BlogEditPostPage /> },
          { path: 'new', element: <BlogNewPostPage /> },
        ],
      },
      {
        path: 'job',
        children: [
          { element: <JobListPage />, index: true },
          { path: 'list', element: <JobListPage /> },
          { path: ':id', element: <JobDetailsPage /> },
          { path: 'new', element: <JobCreatePage /> },
          { path: ':id/edit', element: <JobEditPage /> },
        ],
      },
      {
        path: 'tour',
        children: [
          { element: <TourListPage />, index: true },
          { path: 'list', element: <TourListPage /> },
          { path: ':id', element: <TourDetailsPage /> },
          { path: 'new', element: <TourCreatePage /> },
          { path: ':id/edit', element: <TourEditPage /> },
        ],
      },
      { path: 'file-manager', element: <FileManagerPage /> },
      { path: 'mail', element: <MailPage /> },
      { path: 'chat', element: <ChatPage /> },
      { path: 'calendar', element: <CalendarPage /> },
      { path: 'kanban', element: <KanbanPage /> },
      { path: 'permission', element: <PermissionDeniedPage /> },
      { path: 'blank', element: <BlankPage /> },
    ],
  },
];
